"use client"

import { useSyncExternalStore } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import {
  ChevronsUpDown,
  LogOut,
  Settings,
} from "lucide-react"

// Hydration-safe boolean: false during SSR + first client render, true after
// mount. Used here to defer mounting Radix DropdownMenu (which calls useId)
// until post-hydration, so the sidebar tree doesn't drift Radix IDs between
// SSR and the first client render.
const noopSubscribe = () => () => {}
function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  )
}

import { useAuthStore } from "@/stores/auth-store"
import { useTranslation } from "@/lib/i18n"
import { usePermissions } from "@/hooks/use-permissions"
import { ROUTES } from "@/lib/routes"
import { navText, quickSettings } from "@/lib/settings-nav"
// The signed-in person's name, in any script, and an email address, which
// always reads left to right (RTL-04).
import { UserText } from "@/components/ui/user-text"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

function getUserInitials(name: string): string {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const router = useRouter()
  const initials = getUserInitials(user.name)
  const { t } = useTranslation()
  const hydrated = useHydrated()
  // RC-A: billing is Company Owner only.
  const role = useAuthStore((state) => state.user?.role)
  // Fail-open like the sidebar: while the grants load the links show, and the
  // route gate still refuses a page the role cannot open.
  const access = usePermissions()
  const settings = quickSettings(access.status === "ready" ? access.data?.permissions ?? null : null)
    .filter((leaf) => leaf.url !== ROUTES.settings.billing || role === "Company Owner")

  const handleLogout = () => {
    useAuthStore.getState().logout()
    document.cookie = 'hf-app:access-token=; path=/; max-age=0'
    document.cookie = 'hf-app:refresh-token=; path=/; max-age=0'
    document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0'
    router.push('/login')
  }

  // Render the avatar + name as a plain button during SSR + the first client
  // render. The Radix DropdownMenu (which calls useId for aria-controls)
  // mounts only after hydration — keeps SSR HTML and first-render HTML
  // identical, eliminating the Radix-id hydration mismatch.
  if (!hydrated) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-start text-sm leading-tight">
              <UserText truncate className="font-medium">{user.name}</UserText>
              <UserText dir="ltr" truncate className="text-xs">{user.email}</UserText>
            </div>
            <ChevronsUpDown className="ms-auto size-4" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <UserText truncate className="font-medium">{user.name}</UserText>
                <UserText dir="ltr" truncate className="text-xs">{user.email}</UserText>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="z-[10002] w-[--radix-dropdown-menu-trigger-width] min-w-[220px] rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <UserText truncate className="font-medium">{user.name}</UserText>
                  <UserText dir="ltr" truncate className="text-xs">{user.email}</UserText>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {/* RC-A: billing is Company Owner only — both billing routes
                  403 for every other role, so the entry that leads there is
                  not offered to them. The page itself still explains the rule
                  for anyone who arrives by URL; this only stops the menu
                  advertising a dead end. */}
              {settings.map((leaf) => (
                <DropdownMenuItem key={leaf.url} asChild>
                  <Link href={leaf.url}>
                    <leaf.icon />
                    {navText(t, leaf.title, leaf.i18nKey)}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <Link href="/settings">
                  <Settings />
                  {t('shell.navUser.allSettings')}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
